import { useQuery } from "@tanstack/react-query";
import type { SideNavFeatureCardConfig } from "@wayflyer/flyui";
import { getCheckoutQuoteQueryOptions, getRelativeRateReduction } from "@wayflyer/customer-queries/checkout";
import { useFeatureFlagEnabled, usePostHog } from "posthog-js/react";
import { useIntl } from "react-intl";

const CHECKOUT_FEATURE_CARD_FLAG = "enable_checkout_feature_card";
/** Also the key FlyUI stores the dismissal under, so changing it shows the card again to everyone. */
const CHECKOUT_FEATURE_CARD_ID = "checkout-feature-card";
const VIEWED_SESSION_KEY = `${CHECKOUT_FEATURE_CARD_ID}-viewed`;
const CHECKOUT_FEATURE_CARD_IMAGE_SRC =
  "https://cdn.sanity.io/images/1aendizt/redesign/e03dadd16a91e05ae56f86f20ff3f32659dfbb2f-484x192.png";

/** FlyUI reports an impression per mount, so a full page load would otherwise count again. */
function isFirstViewThisSession(): boolean {
  try {
    if (window.sessionStorage.getItem(VIEWED_SESSION_KEY) !== null) {
      return false;
    }
    window.sessionStorage.setItem(VIEWED_SESSION_KEY, "true");
  } catch {
    // Blocked storage: over-counting views beats losing them.
  }
  return true;
}

export function useCheckoutFeatureCard({
  customerDomain,
  checkoutVisible,
}: {
  customerDomain: string;
  /** Whether the Checkout nav item is showing; the card never promotes a page the nav hides. */
  checkoutVisible: boolean;
}): SideNavFeatureCardConfig | undefined {
  const intl = useIntl();
  const posthog = usePostHog();
  const cardEnabled = useFeatureFlagEnabled(CHECKOUT_FEATURE_CARD_FLAG) === true;
  const { data: quote } = useQuery({
    ...getCheckoutQuoteQueryOptions(customerDomain),
    enabled: cardEnabled && checkoutVisible && !!customerDomain,
  });

  if (!cardEnabled || !checkoutVisible || !quote?.above_saving_floor) {
    return;
  }

  const feeReduction = getRelativeRateReduction(quote.atc_rate, quote.checkout_rate);
  const properties = { fee_reduction: feeReduction };

  return {
    analyticsId: CHECKOUT_FEATURE_CARD_ID,
    title: intl.formatMessage({ id: "checkout feature card title", defaultMessage: "New: Checkout" }),
    content: intl.formatMessage(
      {
        id: "checkout feature card content",
        defaultMessage: "Cut your processing fees by {percent} and reduce payments admin.",
      },
      {
        percent: intl.formatNumber(feeReduction, { style: "percent", maximumFractionDigits: 0 }),
      }
    ),
    image: { src: CHECKOUT_FEATURE_CARD_IMAGE_SRC, alt: "" },
    primaryAction: {
      content: intl.formatMessage({ id: "checkout feature card action", defaultMessage: "Explore Checkout" }),
      url: `/${customerDomain}/checkout`,
      onAction: () => {
        posthog?.capture("checkout_side_nav_card_selected", properties);
      },
    },
    onImpression: () => {
      if (isFirstViewThisSession()) {
        posthog?.capture("checkout_side_nav_card_viewed", properties);
      }
    },
    onDismiss: () => posthog?.capture("checkout_side_nav_card_dismissed", properties),
  };
}
